import { widgetScriptLocation } from 'constants/dom';
import {
    chatHashString,
    defaultChatTitle,
    defaultFakeAgentName,
    defaultTechAgentName,
} from 'constants/general';
import { techAgentId } from 'constants/history';
import { LocalScenario } from 'enums/LocalScenario';
import { MessageType } from 'enums/MessageType';
import { StorageKey } from 'enums/StorageKey';
import { ExpectedParameter } from 'interfaces/ExpectedParameter';
import { MessageData } from 'interfaces/MessageData';
import { WidgetParameters } from 'interfaces/WidgetParameters';
import { DefaultRootState } from 'react-redux';
import { Dispatch } from 'redux';
import { WebViewAdapter } from 'services/webViewAdapterService';
import { WidgetService } from 'services/WidgetService';
import { WidgetEventChannel, widgetEventChannel } from 'src/eventChannels/widget';
import { receiveAgent } from 'state/agents/agents.actions';
import { setIsHoveringButtonsVisible } from 'state/chat/chat.actions';
import { setFeaturesActive } from 'state/features/features.actions';
import { processScenario, sendMessage } from 'state/history/history.actions';
import {
    setCmsUrl,
    setWebviewAdapter,
    updateParameters,
} from 'state/parameters/parameters.actions';
import { ChatStateController } from 'utils/chatState';
import { ChatPerfomance } from 'utils/dtrum';

import { WidgetServices } from '../container';
import { ServiceConfiguration } from '../ServiceConfiguration';

export class WidgetServiceImpl implements WidgetService {
    public services: WidgetServices = null;
    public socket: WebSocket = null;

    readonly eventChannel: WidgetEventChannel = widgetEventChannel;

    private getState: () => DefaultRootState = null;
    private dispatch: Dispatch = null;

    public onLaunch: (params: WidgetParameters) => void = null;

    configure({ widgetStore }: ServiceConfiguration): void {
        this.dispatch = widgetStore.dispatch.bind(widgetStore);
        this.getState = widgetStore.getState.bind(widgetStore);
    }

    private fillDefaultParameters(params: WidgetParameters = {}) {
        params.title ??= defaultChatTitle;

        const scriptUrl = new URL(widgetScriptLocation);
        params.host ??= scriptUrl.host;

        params.secure ??= location.protocol === 'https:';
        params.project ??= 1;
        params.channel ??= 9;

        params.fakeAgent ??= {};
        params.fakeAgent.name ??= defaultTechAgentName;

        params.sharingRoomName ??= 'prod';
    }

    private fillRuntimeParameters(params: WidgetParameters = {}) {
        if (params.debugLevel == null) {
            const storedDebugLevel = localStorage.getItem(StorageKey.DebugLevel);
            if (storedDebugLevel != null) {
                params.debugLevel = parseInt(storedDebugLevel);
            } else {
                params.debugLevel = DEVELOPMENT ? 3 : 0;
            }
        }

        if (params.clientDataJws && !params.getClientDataJws) {
            params.getClientDataJws = () => params.clientDataJws;
        }
    }

    private pluckReceivedParamters(params: WidgetParameters) {
        if (this.dispatch) {
            const expectedParameters: ExpectedParameter[] = [
                {
                    parameterName: 'webViewAdapter',
                    action: setWebviewAdapter,
                    getIsValidPrameter: (webViewAdapter: WebViewAdapter) => !!webViewAdapter,
                },
                {
                    parameterName: 'cmsUrl',
                    action: setCmsUrl,
                    getIsValidPrameter: (cmsUrl?: string) =>
                        Boolean(typeof cmsUrl === 'string' && cmsUrl.trim()),
                },
            ];

            expectedParameters.forEach(({ parameterName, action, getIsValidPrameter }) => {
                const definedParameter = params[parameterName];

                if (getIsValidPrameter(definedParameter)) {
                    this.dispatch(action(definedParameter));
                }
            });
        }
    }

    private onReceivedParameters(params: WidgetParameters) {
        this.pluckReceivedParamters(params);

        const { openByDefault, overrideFeatures, fakeAgent, MfPerfomanceDtrum } = params;
        const oldMfDtrumPerf = this.getState().parameters.MfPerfomanceDtrum;
        if (MfPerfomanceDtrum && MfPerfomanceDtrum !== oldMfDtrumPerf) {
            ChatPerfomance.endAction();
            ChatPerfomance.setMfDtrumPerf(MfPerfomanceDtrum);
        }

        if (openByDefault && !this.getState().chat.open) {
            ChatStateController.toggleChat(true);
        }

        if (overrideFeatures) {
            this.dispatch(setFeaturesActive(overrideFeatures));
        }

        if (fakeAgent) {
            const existingAgent = this.getState().agents[techAgentId];

            if (
                existingAgent.name !== fakeAgent.name ||
                existingAgent.avatar !== fakeAgent.avatar
            ) {
                this.dispatch(
                    receiveAgent({
                        id: techAgentId,
                        name: fakeAgent.name || defaultFakeAgentName,
                        avatar: fakeAgent.avatar,
                    }),
                );
            }
        }

        const hash = location.hash;

        if (hash.startsWith(chatHashString)) {
            ChatStateController.toggleChat(true);

            const hashParam = hash.substring(chatHashString.length);

            const scenarioId = hashParam?.match(/\/scenario\/(.+)/i)?.[1] ?? '';

            if (scenarioId) {
                const isLocalScenario = Object.values(LocalScenario).includes(
                    scenarioId as LocalScenario,
                );
                this.dispatch(
                    processScenario({
                        scenarioId,
                        isLocalScenario,
                    }),
                );
            }

            history?.pushState(null, null, location.href.split('#')[0]);
        }

        if (params.textMessagesToSend) {
            for (const msg of params.textMessagesToSend) {
                this.sendTextMessage(msg);
            }
        }
    }

    private prepare() {
        localStorage.removeItem(StorageKey.TimeAlreadyAwaitedForOperatorMs);
        localStorage.removeItem(StorageKey.OperatorAwaitTimerCleanupTimestamp);
        localStorage.removeItem(StorageKey.ChatOpened);
    }

    public go(params: WidgetParameters = {}) {
        this.prepare();
        this.fillDefaultParameters(params);
        this.fillRuntimeParameters(params);
        this.onLaunch(params);
        this.onReceivedParameters(params);
    }

    public updateParameters(params: WidgetParameters = {}) {
        this.fillRuntimeParameters(params);

        this.dispatch(updateParameters(params));
        this.onReceivedParameters(params);
    }

    public toggle(open?: boolean) {
        const isOpen = this.isOpen();
        open ??= !isOpen;

        if (open != isOpen) {
            ChatStateController.toggleChat(open);
        }
    }

    public open() {
        this.toggle(true);
    }

    public close() {
        this.toggle(false);
    }

    public isOpen() {
        return this.getState().chat.open;
    }

    public sendTextMessage(text: string) {
        this.dispatch(setIsHoveringButtonsVisible(false));
        this.sendMessage({
            type: MessageType.Text,
            text,
        });
    }

    public sendMessage(msg: MessageData) {
        this.dispatch(sendMessage(msg));
    }
}
